import {
  Button,
  Form,
  FormButton,
  FormField,
  FormSubmitHandler,
  OptionType,
} from 'platform/components';
import {Box, HStack, Show} from 'platform/foundation';

import {isNilOrEmpty, isNotNil} from 'ramda-adjunct';

import {featureFlags} from '@dms/feature-flags';
import i18n from '@dms/i18n';

import {suffixTestId, TestIdProps} from 'shared/web';

import {CommentsCardInputSchema} from './CommentsCardInputSchema';

export type CommentCardForm = {
  comment: string;
};

export interface CommentsCardInputProps extends TestIdProps {
  userOptions?: OptionType[];
  defaultComment?: string; // Pre-fill and show Cancel button (edit mode)
  isSending?: boolean;
  onSubmit: FormSubmitHandler<CommentCardForm>;
  onCancel?: () => void;
}

export function CommentsCardInput(props: CommentsCardInputProps) {
  const defaultValues = {
    comment: props.defaultComment ?? '',
  };
  const isEditMode = isNotNil(props.defaultComment);
  const submitTitle = isEditMode ? i18n.t('general.actions.save') : i18n.t('general.actions.send');

  return (
    <Form
      experimentalZodSchema={CommentsCardInputSchema}
      onSubmit={props.onSubmit}
      defaultValues={defaultValues}
      data-testid={suffixTestId('comments', props)}
    >
      {(control, {watch}) => (
        <HStack spacing={4} align="center">
          <Box flex={1}>
            <Show whenFeatureEnabled={featureFlags.NOTIFICATIONS_COMMENT_MENTION}>
              <FormField
                control={control}
                name="comment"
                type="mentionsInput"
                options={props.userOptions}
                placeholder={i18n.t('page.comments.actions.writeAComment')}
                data-testid={suffixTestId('comments-formInput', props)}
              />
            </Show>
            <Show whenFeatureDisabled={featureFlags.NOTIFICATIONS_COMMENT_MENTION}>
              <FormField
                control={control}
                name="comment"
                type="textarea"
                placeholder={i18n.t('page.comments.actions.writeAComment')}
                data-testid={suffixTestId('comments-formInput', props)}
              />
            </Show>
          </Box>
          <HStack spacing={2} align="center">
            <Show when={isEditMode && isNotNil(props.onCancel)}>
              <Button
                variant="secondary"
                title={i18n.t('general.actions.cancel')}
                onClick={props.onCancel}
                data-testid={suffixTestId('comments-formCancel', props)}
              />
            </Show>
            <FormButton
              control={control}
              type="submit"
              variant="primary"
              title={submitTitle}
              isLoading={props.isSending}
              isDisabled={isNilOrEmpty(watch('comment')?.trim())}
              data-testid={suffixTestId('comments-formSubmit', props)}
            />
          </HStack>
        </HStack>
      )}
    </Form>
  );
}
